/**
 * Geçiş Yönetimi ekranlarının zaman ayrıştırma ve biçimlendirmesi.
 *
 * Sunucu bütün zamanları UTC yazar, ancak veritabanından okunan alanlar JSON'a saat
 * dilimi eki OLMADAN çıkar ("2026-08-21T11:20:22"). Tarayıcı böyle bir metni YEREL
 * saat kabul ettiği için ham `new Date(...)` üç saat sapar: az önce yapılan senkron
 * "3 saat önce" görünür. Eki olmayan değerlere bu yüzden Z eklenir; zaten ekli
 * gelenler (bellekteki UTC alanlar, ISO gönderimleri) olduğu gibi ayrıştırılır.
 */
const HAS_TIME_ZONE = /(?:Z|[+-]\d{2}:?\d{2})$/i;
const HAS_TIME_PART = /T\d{2}:\d{2}/;

/** Saat dilimi eki olmayan sunucu tarihlerini UTC kabul ederek ayrıştırır. */
export const parseServerDate = (value?: string | null): Date | null => {
  if (!value) return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  // Saat bileşeni yoksa (yalnızca gün) Z eklemek metni geçersiz kılar.
  const normalized = HAS_TIME_PART.test(trimmed) && !HAS_TIME_ZONE.test(trimmed)
    ? `${trimmed}Z`
    : trimmed;

  const date = new Date(normalized);
  return isNaN(date.getTime()) ? null : date;
};

/**
 * Gösterim saat dilimi tarayıcıya değil Türkiye'ye sabitlenir: kapı ve geçiş
 * kayıtları saha saatiyle okunuyor, yurt dışından bakan bir kullanıcının farklı
 * saat görmesi kayıtların karşılaştırılmasını imkânsız kılar.
 */
const TR_TIME_ZONE = 'Europe/Istanbul';

/** GMT+3 gösterimi; geçersiz/boş değerde tire döner. */
export const formatTrDateTime = (
  value: string | null | undefined,
  options?: { withSeconds?: boolean; fallback?: string },
): string => {
  const date = parseServerDate(value);
  if (!date) return options?.fallback ?? '—';

  return date.toLocaleString('tr-TR', {
    timeZone: TR_TIME_ZONE,
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
    ...(options?.withSeconds ? { second: '2-digit' as const } : {}),
  });
};

/** Verilen sunucu zamanından bu yana geçen süre (ms); ayrıştırılamazsa null. */
export const ageSinceMs = (value: string | null | undefined): number | null => {
  const date = parseServerDate(value);
  return date ? Date.now() - date.getTime() : null;
};
